import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { API_POOL_BASE_URLS, apiPoolManager } from './services/apiPool';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (!SENTRY_DSN) {
  throw new Error('SENTRY_DSN is not set');
}

const collectApiOrigins = (): string[] => {
  const origins = new Set<string>();

  for (const baseUrl of API_POOL_BASE_URLS) {
    try {
      origins.add(new URL(baseUrl).origin);
    } catch {
      //
    }
  }

  const envApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (envApiBaseUrl) {
    try {
      origins.add(new URL(envApiBaseUrl).origin);
    } catch {
      //
    }
  }

  return [...origins];
};

const tracePropagationTargets: (string | RegExp)[] = [
  'localhost',
  /^\/api\//,
  ...collectApiOrigins(),
  /^https:\/\/([a-z0-9-]+\.)?api\.aidasales\.ir/,
  /^https:\/\/stage-api\.aidasales\.net/,
];

// Browser profiling captures JS execution timings (flame graphs in Sentry Performance).
// It requires the Document-Policy: js-profiling response header on the HTML document
// (configured in vite.config.ts for dev/preview and nginx.conf for production static hosting).
//
// tracesSampleRate / profileSessionSampleRate are set to 1.0 for full observability during
// rollout. Lower them in production (e.g. 0.1–0.2) once volume and cost are understood.
Sentry.init({
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  profileSessionSampleRate: 1.0,
  tracePropagationTargets,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
});

const bootstrap = async () => {
  await apiPoolManager.initialize();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
};

bootstrap();
